/**
 * THE HANDLER BADGE'S GEOMETRY — its own module because THREE files have to agree on it
 * and none of them can see the others' arithmetic: `CrmHandlerBadge` paints it,
 * `crmTablePlan` reserves a FIXED desktop column for it, and `CrmContactCard` reserves an
 * inline slot on the mobile card's first line.
 *
 * Exactly the lesson `crmHolderStack.ts` exists to hold: that width lived in two files, one
 * of them forgot the ring, and the overflow badge shipped clipped in half by an
 * `overflow: hidden` cell. Numbers two files must share do not get to live in two files.
 */

export const CRM_HANDLER_FACE = { xs: 18, sm: 22, md: 28 } as const;
export type CrmHandlerBadgeSize = keyof typeof CRM_HANDLER_FACE;

/**
 * The ring around the face.
 *
 * ⚠️ IT IS INFORMATION, NOT DECORATION. For the fleet-default member
 * (`{ pool: 'basic', browse: false }`) this badge is the ONLY cross-agent fact on the row —
 * the holders stack is never fetched and the owner column does not exist on their own tab —
 * so there is nothing beside it to explain it and no panel it can open. The ring colour is
 * what answers "is this mine to act on" without a hover.
 *
 * Drawn on a `content-box`, so it ADDS to the face's footprint. That is the part the first
 * version of the holders stack forgot.
 */
export const CRM_HANDLER_RING = 2;

/** One badge including its ring — the unit a row actually spends. */
export const crmHandlerSlot = (size: CrmHandlerBadgeSize): number => CRM_HANDLER_FACE[size] + CRM_HANDLER_RING * 2;

/**
 * What the desktop column reserves.
 *
 * NOT the slot: the column carries a HEADER (`מטפל`), and the header is what makes an empty
 * cell mean "nobody handles this person" rather than "something failed to render" — which
 * matters precisely because, for most members, this column stands alone. 60 holds the
 * header at 12px with the cell's padding and leaves the 26px face at the reading start.
 */
export const CRM_HANDLER_COLUMN_WIDTH = 60;
